'use client'

import { useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/lib/ui-kit/components/ui/Accordion.component'
import { Pagination } from './Pagination.component'
import { Input } from '@/lib/ui-kit/components/ui/Input.component'
import { Icon } from '@/lib/ui-kit/components/ui/Icon.component'
import { IconType } from '@/lib/ui-kit/components/ui/Icon.types'
import { Button } from '@/lib/ui-kit/components/ui/Button.component'
import VerifiedBadge from './VerifiedBadge.component'
import { WineLotsModal } from './WineLotsModal.component'
import { RawDataModal } from './RawDataModal.component'

interface AccordionTrackerItem {
  txHash: string
  date: string
  transactionType: string
  noBottles: number
  cidVerified: boolean
  wineries: any
  wineriesVerification: any
  noWineries: number
  metadatumLabel: string
  rawMetadata: any
  cid: string
}

interface AccordionTrackerItemProps extends AccordionTrackerItem {
  number: number
}

interface PageCache {
  [key: number]: AccordionTrackerItem[]
}

function middleTruncate(str: string, maxLength = 40) {
  if (str.length <= maxLength) return str

  const half = Math.floor((maxLength - 3) / 2)

  return str.slice(0, half) + '...' + str.slice(str.length - half)
}

const fetchTransactionData = async (
  txHash: string,
  cid: string,
  type: string,
  signatures: any,
) => {
  try {
    const response = await fetch(`/api/tracker-transaction-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        hash: txHash,
        cid: cid,
        type: type,
        signatures: signatures,
      }),
    })

    if (response.status !== 200) {
      throw new Error('Failed to fetch transaction data.')
    }

    return await response.json()
  } catch (error) {
    throw error
  }
}

const AccordionTrackerItem = ({
  number,
  txHash,
  date,
  transactionType,
  noBottles,
  cidVerified,
  wineries,
  wineriesVerification,
  noWineries,
  metadatumLabel,
  rawMetadata,
  cid,
}: AccordionTrackerItemProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isRawDataModalOpen, setIsRawDataModalOpen] = useState(false)
  const [selectedWineryData, setSelectedWineryData] = useState<any[]>([])
  const [selectedWineryVerification, setSelectedWineryVerification] = useState<
    any[]
  >([])
  return (
    <AccordionItem value={String(number)}>
      <AccordionTrigger className="px-6 xl:px-10 xl:hover:text-base-light">
        <div className="grid w-full grid-cols-1 gap-x-4 py-4 pr-10 text-left text-[1.25rem] font-medium leading-[1.75rem] tracking-tight xl:grid-cols-12 xl:pr-6 xl:text-[1.37rem] xl:leading-[2.125rem]">
          <div className="paragraph-xs text-gray-600 xl:hidden">Tx Hash</div>
          <div className="hidden items-center xl:col-span-6 2xl:flex">
            {middleTruncate(txHash, 50)}
          </div>
          <div className="hidden items-center xl:col-span-6 xl:flex 2xl:hidden">
            {middleTruncate(txHash, 30)}
          </div>
          <div className="hidden items-center md:flex xl:col-span-6 xl:hidden">
            {middleTruncate(txHash, 50)}
          </div>
          <div className="flex items-center md:hidden xl:col-span-6">
            {middleTruncate(txHash, 20)}
          </div>
          <div className="paragraph-xs mt-2 text-gray-600 xl:hidden">Date</div>
          <div className="flex items-center xl:col-span-2">{date}</div>
          <div className="paragraph-xs mb-1.5 mt-2 text-gray-600 xl:hidden">
            Transaction Type
          </div>
          <div className="flex items-center xl:col-span-2">
            <div className="paragraph-s inline-block rounded-full bg-base-dark px-4 py-2 text-center font-medium text-base-light">
              {transactionType}
            </div>
          </div>
          <div className="paragraph-xs mt-2 text-gray-600 xl:hidden">
            No. Bottles
          </div>
          <div className="flex items-center xl:col-span-2 xl:justify-end">
            {noBottles}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-6 xl:px-10">
        <div className="mb-6 flex flex-col xl:mb-10">
          <div className="paragraph-l mb-1.5 font-semibold xl:mb-2">
            Metadata Hash
          </div>
          <div className="paragraph-s hidden md:block">{txHash}</div>
          <div className="paragraph-s md:hidden">
            {middleTruncate(txHash, 35)}
          </div>
        </div>
        <div className="mb-4 grid xl:grid-cols-12 xl:gap-y-2">
          <div className="paragraph-s mb-1.5 font-semibold xl:col-span-2 xl:mb-0">
            Metadatum Label
          </div>
          <div className="paragraph-s mb-3 xl:col-span-10 xl:mb-0">
            {metadatumLabel}
          </div>
          <div className="paragraph-s mb-1.5 font-semibold xl:col-span-2 xl:mb-0">
            Value
          </div>
          <div className="paragraph-s mb-3 xl:col-span-10 xl:mb-0">
            <button
              onClick={() => setIsRawDataModalOpen(true)}
              className="inline-block cursor-pointer underline hover:opacity-75"
            >
              View Raw Data
            </button>
          </div>
          <div className="paragraph-s flex items-center font-semibold xl:col-span-2">
            Content Identifier
          </div>
          <div className="mb-1.5 flex items-center xl:col-span-10 xl:mb-0">
            <div className="paragraph-s hidden md:block">{cid}</div>
            <div className="paragraph-s md:hidden">
              {middleTruncate(cid, 25)}
            </div>
            {cidVerified && (
              <div className="ml-4 md:ml-10">
                <VerifiedBadge />
              </div>
            )}
          </div>
          <div className="paragraph-s mb-1.5 font-semibold xl:col-span-2 xl:mb-0">
            {transactionType === 'SCM' ? 'Wineries' : 'Certification Entities'}
          </div>
          <div className="paragraph-s xl:col-span-10">{noWineries}</div>
        </div>
        <div className="grid gap-5 xl:grid-cols-2">
          {Object.entries(wineries).map(([winery, data]: any) => {
            return (
              <div
                key={winery}
                className="relative flex h-[17.5rem] flex-col justify-center bg-gray-200 px-6"
              >
                <div className="heading-l font-medium">
                  {data[0]?.produced_by || 'National Wine Agency'}
                </div>
                <div className="paragraph-l">{data[0]?.producer_address}</div>
                <div className="absolute bottom-6 left-6">
                  <Button
                    variant="primary"
                    className="mr-2 rounded-full px-6"
                    size="lg"
                    onClick={() => {
                      setSelectedWineryData(data)
                      setSelectedWineryVerification(
                        wineriesVerification[winery],
                      )
                      setIsModalOpen(true)
                    }}
                  >
                    {transactionType === 'SCM'
                      ? 'View Wine Lots'
                      : 'View Certificate'}
                  </Button>
                </div>
                {wineriesVerification[winery].verified && (
                  <div className="absolute right-6 top-6">
                    <VerifiedBadge />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <WineLotsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          wineryData={selectedWineryData}
          wineryVerification={selectedWineryVerification}
        />
        <RawDataModal
          isOpen={isRawDataModalOpen}
          onClose={() => setIsRawDataModalOpen(false)}
          data={rawMetadata}
        />
      </AccordionContent>
    </AccordionItem>
  )
}

export const AccordionTracker = ({ items }: any) => {
  const [transactionItems, setTransactionItems] = useState<
    AccordionTrackerItem[]
  >([])
  const [previousItems, setPreviousItems] = useState<AccordionTrackerItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCache, setPageCache] = useState<PageCache>({})
  const itemsPerPage = 5
  const [totalItems, setTotalItems] = useState(0)
  const router = useRouter()
  const searchParams = useSearchParams()
  const searchValue = searchParams.get('transaction') || ''
  const [isSearching, setIsSearching] = useState(false)
  const [searchCache, setSearchCache] = useState<{
    [key: string]: AccordionTrackerItem[]
  }>({})
  const [lastSearchPrefix, setLastSearchPrefix] = useState('')

  const handleTransactionPromises = (transactions: any) => {
    return transactions.map(async (transaction: any) => {
      const data = await fetchTransactionData(
        transaction.tx_hash,
        transaction.json_metadata.cid,
        transaction.json_metadata.t,
        transaction.json_metadata.t === 'conformityCert'
          ? {
              0: {
                s: transaction.json_metadata.s,
                pk: transaction.json_metadata.pk,
              },
            }
          : transaction.json_metadata.d,
      )

      let noBottles = 0

      if (transaction.json_metadata.t === 'scm') {
        Object.values(data.transactionOffchainData).map((offchainData: any) => {
          offchainData.map((data: any) => {
            noBottles += data.number_of_bottles
          })
        })
      }

      if (transaction.json_metadata.t === 'conformityCert') {
        Object.values(data.transactionOffchainData).map((offchainData: any) => {
          offchainData.products.map((data: any) => {
            noBottles += data.bottle_count_in_lot
          })
        })
      }

      return {
        txHash: transaction.tx_hash,
        date: new Date(
          data.transactionOnchainData.block_time * 1000,
        ).toLocaleDateString(),
        transactionType:
          transaction.json_metadata.t === 'conformityCert'
            ? 'Conformity Certificate'
            : 'SCM',
        noBottles: noBottles,
        cidVerified: data.cidVerified,
        wineries: data.transactionOffchainData,
        wineriesVerification: data.wineriesVerification,
        noWineries: Object.keys(data.transactionOffchainData).length,
        metadatumLabel: '1904',
        rawMetadata: transaction,
        cid: transaction.json_metadata.cid,
      }
    })
  }

  const searchTransactions = async (query: string) => {
    if (!items?.transactions) return []

    setIsSearching(true)
    try {
      const searchPrefix = query.slice(0, 10)

      if (searchCache[searchPrefix]) {
        setIsSearching(false)
        return searchCache[searchPrefix]
      }

      const matchingTransactions = items.transactions.filter(
        (transaction: any) =>
          transaction.tx_hash.toLowerCase().includes(query.toLowerCase()),
      )

      const transactionPromises =
        handleTransactionPromises(matchingTransactions)

      const results = await Promise.all(transactionPromises)

      setSearchCache((prev) => ({
        ...prev,
        [searchPrefix]: results,
      }))

      return results
    } catch (error) {
      return []
    } finally {
      setIsSearching(false)
    }
  }

  const handleSearchChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString())
    if (value) {
      params.set('transaction', value)
    } else {
      params.delete('transaction')
    }
    router.push(`?${params.toString()}`, { scroll: false })

    if (value.length >= 10) {
      const searchPrefix = value.slice(0, 10)

      if (searchPrefix !== lastSearchPrefix) {
        setLastSearchPrefix(searchPrefix)
        searchTransactions(value).then((results) => {
          setTransactionItems(results)
        })
      }
    } else {
      setLastSearchPrefix('')
      if (pageCache[currentPage]) {
        setTransactionItems(pageCache[currentPage])
      } else {
        fetchPageTransactionData()
      }
    }
  }

  const fetchPageTransactionData = async () => {
    if (!items?.transactions) return

    try {
      setTotalItems(items.transactions.length)
      const startIndex = (currentPage - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage

      setPreviousItems(transactionItems)

      if (pageCache[currentPage]) {
        setTransactionItems(pageCache[currentPage])
        setIsLoading(false)
        return
      }

      const pageTransactions = items.transactions.slice(startIndex, endIndex)

      const transactionPromises = handleTransactionPromises(pageTransactions)

      const resolvedTransactions = await Promise.all(transactionPromises)

      setPageCache((prev) => ({
        ...prev,
        [currentPage]: resolvedTransactions,
      }))

      setTransactionItems(resolvedTransactions)
    } catch (err) {
      setError('Failed to fetch transaction data')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setIsLoading(true)
    if (!searchValue || searchValue.length < 10) {
      fetchPageTransactionData()
    }
  }, [items, currentPage])

  useEffect(() => {
    setPageCache({})
  }, [items])

  useEffect(() => {
    if (searchValue && searchValue.length >= 10) {
      searchTransactions(searchValue).then((results) => {
        setTransactionItems(results)
        setIsLoading(false)
        // Add scroll behavior
        const element = document.getElementById('wine-transaction')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      })
    }
  }, [])

  if (!items) {
    return (
      <div className="paragraph-xl flex w-full justify-center">
        Failed to load transactions.
      </div>
    )
  }

  const isInitialLoad =
    isLoading &&
    previousItems.length === 0 &&
    (!searchValue || searchValue.length < 10)
  const renderItems = isSearching
    ? previousItems
    : searchValue.length >= 10
      ? transactionItems
      : isLoading && !isInitialLoad
        ? previousItems
        : transactionItems

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 xl:grid-cols-5">
        <div className="heading-l mb-6 hidden items-center justify-center xl:col-span-3 xl:mb-10 xl:flex xl:justify-start">
          Recent transactions
        </div>
        <div className="relative mb-6 xl:col-span-2 xl:mb-10">
          <Input
            className="paragraph-l rounded-full border-none px-16 py-4 outline-none"
            type="text"
            placeholder="Search transactions"
            value={searchValue}
            onChange={(e) => {
              const value = e.target.value
              handleSearchChange(value)
            }}
          />
          <Icon
            className="absolute left-5 top-1/2 -translate-y-1/2"
            type={IconType.search}
          />
          {searchValue && (
            <div
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer px-2 py-2"
              onClick={() => {
                handleSearchChange('')
                if (pageCache[currentPage]) {
                  setTransactionItems(pageCache[currentPage])
                } else {
                  fetchPageTransactionData()
                }
                setTotalItems(items?.transactions?.length || 0)
              }}
            >
              <Icon type={IconType.close} />
            </div>
          )}
        </div>
      </div>
      <div className="hidden grid-cols-12 gap-x-4 border-b border-gray-400 pb-2.5 pl-6 pr-[8rem] xl:grid xl:pl-10 xl:pr-[8rem]">
        <div className="paragraph-s col-span-6 text-gray-600">Tx Hash</div>
        <div className="paragraph-s col-span-2 text-gray-600">Date</div>
        <div className="paragraph-s col-span-2 text-gray-600">
          Transaction Type
        </div>
        <div className="paragraph-s col-span-2 text-right text-gray-600">
          No. Bottles
        </div>
      </div>
      <div className={`transition-opacity duration-300`}>
        {isInitialLoad ? (
          <div className="paragraph-xl flex h-[31.875rem] w-full items-center justify-center">
            Loading transaction data...
          </div>
        ) : (
          <div
            className={`${
              isLoading
                ? 'pointer-events-none animate-pulse opacity-30'
                : 'opacity-100'
            }`}
            style={{ minHeight: `${itemsPerPage * 76}px` }}
          >
            <Accordion
              type="single"
              collapsible
              className="w-full"
              key={renderItems.map((item) => item.txHash).join(',')}
            >
              {renderItems.map((transaction, index) => (
                <AccordionTrackerItem
                  key={transaction.txHash}
                  number={index + 1}
                  txHash={transaction.txHash}
                  date={transaction.date}
                  transactionType={transaction.transactionType}
                  noBottles={transaction.noBottles}
                  cidVerified={transaction.cidVerified}
                  metadatumLabel={transaction.metadatumLabel}
                  rawMetadata={transaction.rawMetadata}
                  wineries={transaction.wineries}
                  wineriesVerification={transaction.wineriesVerification}
                  noWineries={transaction.noWineries}
                  cid={transaction.cid}
                />
              ))}
            </Accordion>
          </div>
        )}
      </div>
      {error && (
        <div className="paragraph-xl text-red-500 mt-4 flex w-full justify-center">
          {error}
        </div>
      )}
      {searchValue.length < 10 && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  )
}
